import { z } from "zod/v4";
import { DoctorsIdSchema } from "../types/zodSchemas.js";
const amountSchema = z.string().regex(/^\d{1,9}\.\d{2}$/);
const dayCountSchema = z.number().min(0).max(366);
export const TesePayrollExportSchema = z
    .strictObject({
    version: z.union([z.literal(1), z.literal(2)]),
    id: z.uuid(),
    ownerId: DoctorsIdSchema,
    generatedAt: z.iso.datetime(),
    pdfFileName: z.string().min(1).max(255),
    pdfSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
    snapshotSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    revision: z.number().int().positive().optional(),
    employee: z.strictObject({
        id: z.string().min(1).max(100),
        fullname: z.string().min(1).max(200),
        socialSecurityNumber: z
            .string()
            .regex(/^\d{15}$/)
            .nullable(),
    }),
    periodStart: z.iso.date(),
    periodEnd: z.iso.date(),
    salaryType: z.enum(["net", "gross"]),
    remuneration: amountSchema,
    seniorityBonus: z.strictObject({
        grossAmount: amountSchema,
        netAmount: amountSchema,
    }),
    paidHours: amountSchema.nullable(),
    workedDays: dayCountSchema,
    paidLeaveDays: dayCountSchema,
    paidLeaveRanges: z
        .array(z.strictObject({
        startDate: z.iso.date(),
        endDate: z.iso.date(),
        returnDate: z.iso.date(),
        countedDays: dayCountSchema,
    }))
        .max(100),
    specialLeaveDays: dayCountSchema,
    // Optional for compatibility with snapshots generated before detail support.
    specialLeaveAbsences: z
        .array(z.strictObject({
        absenceDate: z.iso.date(),
        partOfDay: z.enum(["full_day", "morning", "afternoon"]),
        countedDays: dayCountSchema,
        reason: z.string().max(200),
    }))
        .max(100)
        .optional(),
    unpaidLeaveDays: dayCountSchema,
})
    .refine((value) => value.version === 1
    ? value.pdfSha256 !== null
    : value.pdfSha256 === null &&
        !!value.snapshotSha256 &&
        !!value.revision, "Invalid payroll source identity");
// Additional filling data is read from the frozen archive, without rewriting its export or hash.
export const savedPayslipFillingDataSchema = z.strictObject({
    payroll: TesePayrollExportSchema,
    mealVoucherEmployeeAmount: amountSchema.nullable(),
});
export const savedPayslipCursorSchema = z.strictObject({
    savedAt: z.iso.datetime(),
    id: z.uuid(),
});
export const listSavedPayslipsInputSchema = z.strictObject({
    secretaryId: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(100).default(30),
    cursor: savedPayslipCursorSchema.nullish(),
    // tRPC infinite queries inject direction; only older-record pagination is supported.
    direction: z.literal("forward").optional(),
});
export const savedPayslipsPageSchema = z.strictObject({
    items: z.array(TesePayrollExportSchema).max(100),
    nextCursor: savedPayslipCursorSchema.nullable(),
});
export const savePayslipInputSchema = z.strictObject({
    requestId: z.uuid(),
    secretaryId: z.string().min(1),
    referenceDate: z.iso.date(),
    grossSalary: z
        .string()
        .regex(/^\d+(?:[.,]\d{1,2})?$/)
        .optional(),
    specialLeaveReasons: z
        .record(z.string(), z.string().trim().max(200))
        .default({}),
});
export const savedPayslipSnapshotSchema = z.strictObject({
    version: z.literal(1),
    payroll: TesePayrollExportSchema,
    document: z.strictObject({
        fileName: z.string().min(1).max(255),
        pages: z.array(z.array(z.string())).min(1).max(100),
    }),
    calculation: z.json(),
});
export const TesePayslipDownloadSchema = z.strictObject({
    fileName: z.string().min(1),
    pdfBase64: z.string().min(1),
    tesePayroll: TesePayrollExportSchema.nullable(),
});
export const TesePayrollGeneratedMessageSchema = z.strictObject({
    type: z.literal("CADENDAR_TESE_PAYROLL_GENERATED"),
    payload: TesePayrollExportSchema,
});
export const TesePayrollReceiptSchema = z.strictObject({
    type: z.literal("CADENDAR_TESE_PAYROLL_RECEIPT"),
    id: z.uuid(),
    ok: z.boolean(),
    error: z.string().max(300).optional(),
});
//# sourceMappingURL=tesePayroll.js.map